/**
 * Keyless YouTube playlist helpers.
 *
 * Enumerating a playlist without a Data API key means going through the IFrame
 * Player API (`player.getPlaylist()`), then YouTube's public oEmbed endpoint for
 * titles. Thumbnails need no request at all — `i.ytimg.com` serves them as plain
 * static images. Nothing here needs a key, a quota, or a secret.
 */

const IFRAME_API_SRC = "https://www.youtube.com/iframe_api";

let iframeApiPromise = null;

/**
 * Loads the YouTube IFrame Player API once per page.
 *
 * The API signals readiness through a single global callback, so this has to be
 * a shared singleton — two components racing to load it would clobber each
 * other's `onYouTubeIframeAPIReady`.
 */
export function loadYoutubeIframeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (iframeApiPromise) return iframeApiPromise;

  iframeApiPromise = new Promise((resolve, reject) => {
    const previousCallback = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve(window.YT);
    };

    const script = document.createElement("script");
    script.src = IFRAME_API_SRC;
    script.async = true;
    script.onerror = () => {
      iframeApiPromise = null;
      reject(new Error("Failed to load the YouTube IFrame Player API"));
    };

    document.head.appendChild(script);
  });

  return iframeApiPromise;
}

export function youtubeThumbnailUrl(videoId) {
  return `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
}

export function extractYoutubePlaylistId(url = "") {
  return String(url || "").match(/[?&]list=([a-zA-Z0-9_-]+)/)?.[1] || null;
}

/**
 * Resolves to a playlist's ordered video ids via a throwaway, invisible IFrame
 * player — the same keyless technique PlaylistViewer uses, minus the playback
 * wiring it needs and this one-shot query doesn't. Falls back to `[]` after 8s so
 * a broken or private playlist can't leak the hidden player forever.
 */
export function fetchYoutubePlaylistVideoIds(playlistId) {
  return loadYoutubeIframeApi()
    .then(
      (YT) =>
        new Promise((resolve) => {
          const host = document.createElement("div");
          host.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;";
          document.body.appendChild(host);

          let settled = false;
          let player;
          const finish = (ids) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            player.destroy();
            host.remove();
            resolve(ids);
          };
          const timer = setTimeout(() => finish([]), 8000);

          const readPlaylist = () => {
            const ids = player.getPlaylist?.();
            if (Array.isArray(ids) && ids.length) finish(ids);
          };

          player = new YT.Player(host, {
            playerVars: { listType: "playlist", list: playlistId },
            events: {
              onReady: readPlaylist,
              onStateChange: readPlaylist,
              onError: () => finish([]),
            },
          });
        })
    )
    .catch(() => []);
}

/**
 * A playlist may legitimately contain the same video twice, so the position has
 * to be part of the identity — keying on the video id alone would collide.
 */
export function youtubeItemId(index, videoId) {
  return `yt:${index}:${videoId}`;
}

async function fetchYoutubeTitle(videoId) {
  const target = encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`);

  try {
    const res = await fetch(
      `https://www.youtube.com/oembed?url=${target}&format=json`
    );

    if (!res.ok) return "";

    const data = await res.json();
    return data?.title || "";
  } catch {
    // Private, deleted, or region-blocked video — the caller falls back to a
    // numbered title rather than dropping the row.
    return "";
  }
}

/**
 * ponytail: one oEmbed request per video. Fine for the 2-10 video playlists we
 * have. If playlists ever run long, fetch titles server-side (Data API + cache)
 * and hand them in instead.
 */
export async function fetchYoutubeTitles(videoIds = []) {
  const unique = [...new Set(videoIds)];

  const entries = await Promise.all(
    unique.map(async (videoId) => [videoId, await fetchYoutubeTitle(videoId)])
  );

  return Object.fromEntries(entries.filter(([, title]) => title));
}

/**
 * The one place the viewer's list is assembled.
 *
 * Items are deliberately source-agnostic (`kind` says how to play them) so that
 * folding in a project's other videos later — a Drive walkthrough, another demo
 * clip — is a change to `extraItems` here and nowhere else.
 */
export function buildPlaylistItems(videoIds = [], titles = {}, extraItems = []) {
  const youtubeItems = videoIds.map((videoId, index) => ({
    id: youtubeItemId(index, videoId),
    kind: "youtube",
    videoId,
    index,
    title: titles[videoId] || `Video ${index + 1}`,
    thumbnailUrl: youtubeThumbnailUrl(videoId),
  }));

  const extras = extraItems.map((item, i) => ({
    thumbnailUrl: "",
    ...item,
    id: item.id || `extra:${i}`,
    kind: item.kind || "embed",
    title: item.title || `Video ${youtubeItems.length + i + 1}`,
  }));

  return [...youtubeItems, ...extras];
}
